import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Slider } from '../components/ui/slider';
import ProductCard from '../components/ProductCard';
import { supabase } from '../supabaseClient';
import { SlidersHorizontal } from "lucide-react";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedFabric, setSelectedFabric] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sortBy, setSortBy] = useState('default');

  const categories = ['all', 'women', 'men', 'homeware'];
  const fabrics = ['all', 'Organic Cotton', 'Linen', 'Linen Blend', 'Hemp Silk', 'Bamboo', 'Hemp Canvas'];
  const sizes = ['all', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size'];

  const isHomeware = selectedCategory === 'homeware';

  // Fetch all live products
  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_live', true);

      if (!error) {
        setProducts(data);
      }
    };
    fetchProducts();
  }, []);

  // Apply filters whenever anything changes
  useEffect(() => {
    let result = [...products];

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(p =>
        p.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Fabric filter — only for non-homeware
    if (!isHomeware && selectedFabric !== 'all') {
      result = result.filter(p =>
        p.fabric?.toLowerCase() === selectedFabric.toLowerCase()
      );
    }

    // Size filter — only for non-homeware
    if (!isHomeware && selectedSize !== 'all') {
      result = result.filter(p =>
        Array.isArray(p.sizes)
          ? p.sizes.includes(selectedSize)
          : p.size === selectedSize
      );
    }

    // Price filter
    result = result.filter(p =>
      p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Sorting
    if (sortBy === 'price_asc') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_desc') {
      result.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name_asc') {
      result.sort((a, b) => a.name?.localeCompare(b.name));
    } else if (sortBy === 'name_desc') {
      result.sort((a, b) => b.name?.localeCompare(a.name));
    } else if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    setFilteredProducts(result);
  }, [products, selectedCategory, selectedFabric, selectedSize, priceRange, sortBy, isHomeware]);

  // Reset fabric/size when switching to homeware
  useEffect(() => {
    if (isHomeware) {
      setSelectedFabric('all');
      setSelectedSize('all');
    }
  }, [isHomeware]);

  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedFabric('all');
    setSelectedSize('all');
    setPriceRange([0, 10000]);
    setSortBy('default');
    setSearchParams({});
  };

  const FilterSections = () => (
    <>
      {/* Category */}
      <div>
        <h4 className="text-sm uppercase tracking-wider text-charcoal mb-4 font-medium">
          Category
        </h4>
        <div className="space-y-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`block w-full text-left py-2 px-3 rounded text-sm transition-colors ${
                selectedCategory === category
                  ? 'bg-bronze text-charcoal font-medium'
                  : 'text-[#6B665F] hover:bg-beige'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-[#E5E0D8] my-4" />

      {/* Sort — always visible */}
      <div>
        <h4 className="text-sm uppercase tracking-wider text-charcoal mb-4 font-medium">
          Sort By
        </h4>
        <div className="space-y-2">
          {[
            { value: 'default', label: 'Default' },
            { value: 'newest', label: 'Newest First' },
            { value: 'price_asc', label: 'Price: Low to High' },
            { value: 'price_desc', label: 'Price: High to Low' },
            { value: 'name_asc', label: 'Name: A to Z' },
            { value: 'name_desc', label: 'Name: Z to A' },
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={`block w-full text-left py-2 px-3 rounded text-sm transition-colors ${
                sortBy === option.value
                  ? 'bg-bronze text-charcoal font-medium'
                  : 'text-[#6B665F] hover:bg-beige'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-[#E5E0D8] my-4" />

      {/* Price Range — always visible */}
      <div>
        <h4 className="text-sm uppercase tracking-wider text-charcoal mb-4 font-medium">
          Price Range
        </h4>
        <Slider
          min={0}
          max={10000}
          step={100}
          value={priceRange}
          onValueChange={setPriceRange}
          className="mb-4 [&_[role=slider]]:bg-bronze [&_[role=slider]]:border-bronze"
        />
        <div className="flex justify-between text-sm text-[#6B665F]">
          <span>₹{priceRange[0]}</span>
          <span>₹{priceRange[1]}</span>
        </div>
      </div>

      {/* Fabric & Size — hidden for homeware */}
      {!isHomeware && (
        <>
          <div className="h-px bg-[#E5E0D8] my-4" />

          {/* Fabric */}
          <div>
            <h4 className="text-sm uppercase tracking-wider text-charcoal mb-4 font-medium">
              Fabric
            </h4>
            <div className="space-y-2">
              {fabrics.map(fabric => (
                <button
                  key={fabric}
                  onClick={() => setSelectedFabric(fabric)}
                  className={`block w-full text-left py-2 px-3 rounded text-sm transition-colors ${
                    selectedFabric === fabric
                      ? 'bg-bronze text-charcoal font-medium'
                      : 'text-[#6B665F] hover:bg-beige'
                  }`}
                >
                  {fabric.charAt(0).toUpperCase() + fabric.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="h-px bg-[#E5E0D8] my-4" />

          {/* Size */}
          <div>
            <h4 className="text-sm uppercase tracking-wider text-charcoal mb-4 font-medium">
              Size
            </h4>
            <div className="flex flex-wrap gap-2">
              {sizes.map(size => (
                <button
                  key={size}
                  onClick={() => setSelectedSize(size)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full transition-all ${
                    selectedSize === size
                      ? 'bg-[#EAD7CF] text-[#4A2F2A] border border-[#8C6A3E] shadow-sm font-semibold scale-105'
                      : 'bg-white text-charcoal border border-[#E5E0D8] hover:border-[#B08D57] hover:shadow-sm'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal mb-4">
            Shop Collection
          </h1>
          <p className="text-[#6B665F] font-light text-lg">
            Ethical luxury for every aspect of life
          </p>
        </div>

        {/* Mobile Filter Trigger */}
        <div className="lg:hidden mb-6 flex items-center gap-2">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-2 text-charcoal text-sm font-medium"
          >
            <SlidersHorizontal size={18} />
            Filters
          </button>
        </div>

        {/* Mobile Filter Panel */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 bg-black/40">
            <div className="absolute left-0 top-0 h-full w-[85%] bg-white p-6 shadow-xl overflow-y-auto space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-serif text-xl">Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-sm underline"
                >
                  Close
                </button>
              </div>
              <FilterSections />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block lg:col-span-1 space-y-8">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-charcoal">Filters</h3>
              <button
                onClick={resetFilters}
                className="text-xs px-3 py-1.5 rounded-full border border-[#B08D57] text-[#4A2F2A] bg-[#F5EFE6]"
              >
                Reset
              </button>
            </div>
            <FilterSections />
          </aside>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            <p className="text-sm text-[#6B665F] mb-6">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'Product' : 'Products'}
            </p>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-20 bg-white/50 rounded-2xl">
                <div className="w-20 h-20 bg-[#f0ebe4] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#ba8245]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h2 className="font-serif text-2xl text-charcoal mb-2">No Products Found</h2>
                <p className="text-[#6B665F] mb-6">
                  No products match your current filters.
                </p>
                <button
                  onClick={resetFilters}
                  className="text-sm px-6 py-2 rounded-full border border-[#B08D57] text-[#4A2F2A] bg-[#F5EFE6] hover:bg-[#ede3d6] transition"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;